import withKycRequired from "@rentiohq/shared-frontend/dist/hoc/withKycRequired";
import * as authSelectors from "@rentiohq/shared-frontend/dist/redux/auth/auth.selectors";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import * as bankAccountActions from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.actions";
import * as bankAccountApi from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.api";
import * as bankAccountSelectors from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.selectors";
import * as paymentRequestApi from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.api";
import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import { EPaymentRequestStatus } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { VopStatusBadge } from "@rentiohq/web-shared/dist/components";
import {
  Box,
  Button,
  MainCard,
  Typography,
  Divider,
  Stack,
  RadioButtonsGroup,
  SpinningLoader,
  Link,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import queryString from "query-string";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useNavigate,
  useParams,
  useLocation,
  Navigate,
} from "react-router-dom";
import { useRedirectIfPlannedCollection } from "../../useRedirectIfPlannedCollection";
import { IbanAccountSelectStyles } from "./IbanAccountSelect.styles";

let autoSubmitInterval: NodeJS.Timeout | undefined;

const IbanAccountSelectBase = () => {
  const sx = IbanAccountSelectStyles();
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const {
    detail: paymentRequest,
    fetchError: paymentRequestError,
    isFetching: isFetchingPaymentRequest,
  } = paymentRequestHooks.useDetail({
    id: paymentRequestId,
    shouldRefetch: false,
  });

  const isPlannedCollection = useRedirectIfPlannedCollection(paymentRequest);

  const user = useSelector((state: IPartialRootState) =>
    authSelectors.getUser(state),
  );

  const ibans = useSelector((state: IPartialRootState) =>
    bankAccountSelectors.getContactBankAccounts(state, {
      contactId: user?.personalContactId,
    }),
  );

  const isFetchingIbans = useSelector((state: IPartialRootState) =>
    bankAccountSelectors.isFetchingContactBankAccounts(state, {
      contactId: user?.personalContactId,
    }),
  );
  const fetchIbanError = useSelector((state: IPartialRootState) =>
    bankAccountSelectors.contactBankAccountsFetchError(state, {
      contactId: user?.personalContactId,
    }),
  );

  const { ibanId: ibanIdRaw } = queryString.parse(location.search) as {
    ibanId?: string;
  };
  const ibanId = ibanIdRaw ? parseInt(ibanIdRaw, 0) : undefined;

  const [selectedIbanId, setSelectedIbanId] = React.useState<
    number | undefined
  >(ibanId);
  const [isPaying, setIsPaying] = React.useState(false);

  React.useEffect(() => {
    dispatch(
      bankAccountActions.getContactBankAccountsStart.getAction({
        contactId: user?.personalContactId,
      }),
    );
    handleAutoSubmit();
    return () => {
      if (autoSubmitInterval) {
        clearInterval(autoSubmitInterval);
      }
    };
  }, []);

  React.useEffect(() => {
    if (fetchIbanError) {
      enqueueSnackbar({
        message: fetchIbanError?.message,
        variant: "error",
      });
    }
  }, [fetchIbanError]);

  React.useEffect(() => {
    if (paymentRequestError) {
      enqueueSnackbar({
        message: paymentRequestError?.message,
        variant: "error",
      });
    }
  }, [paymentRequestError]);

  React.useEffect(() => {
    if (
      paymentRequest &&
      paymentRequest.status === EPaymentRequestStatus.Paid
    ) {
      enqueueSnackbar({
        message: getLocalizedText("payment.error.already_paid.title"),
        variant: "error",
      });
      navigate("/payments");
    }
  }, [paymentRequest]);

  const isRecurring = () => {
    const { type = "once" } = queryString.parse(location.search) as {
      type?: string;
    };
    return type === "recurring";
  };

  const isAutoSubmit = () => {
    const { autoSubmit } = queryString.parse(location.search) as {
      autoSubmit?: string;
    };

    return autoSubmit === "1";
  };

  const fetchIban = async (id: number) => {
    if (!user) {
      return;
    }

    const { data: ibans } = await bankAccountApi.getBankAccountsForContact(
      user.personalContactId,
    );
    return ibans.find(randomIban => id === randomIban.id);
  };

  const getMandateCallbackUrl = (ibanId: number) => {
    const result = queryString.parseUrl(window.location.href);
    result.query.ibanId = `${ibanId}`;
    result.query.autoSubmit = "1";
    return queryString.stringifyUrl(result);
  };

  const startAutoSubmitInterval = () => {
    if (autoSubmitInterval) {
      return;
    }
    autoSubmitInterval = setInterval(handleAutoSubmit, 1000);
  };

  const clearAutoSubmitInterval = () => {
    if (!autoSubmitInterval) {
      return;
    }
    clearInterval(autoSubmitInterval);
    autoSubmitInterval = undefined;
  };

  const handlePayNow = async () => {
    if (!paymentRequestId || isPlannedCollection) {
      return;
    }

    if (!selectedIbanId) {
      return;
    }

    setIsPaying(true);

    try {
      const iban = await fetchIban(selectedIbanId);
      if (!iban) {
        throw new Error("No IBAN");
      }

      if (!iban.mandateApprovedAt) {
        const { data: mandateRequest } = await paymentRequestApi.getMandate(
          selectedIbanId,
          getMandateCallbackUrl(selectedIbanId),
        );

        const redirectUrl = mandateRequest.RedirectUrl;
        window.open(redirectUrl, "_self");
        return;
      }
    } catch (unknownError) {
      setIsPaying(false);
      return;
    }

    try {
      await paymentRequestApi.payPaymentRequest(
        paymentRequestId,
        selectedIbanId,
        isRecurring(),
      );
      navigate(`/payment/${paymentRequestId}/status`);
    } catch (unknownError) {
      const error = unknownError as any;
      enqueueSnackbar({
        message: error?.message || "Something went wrong",
        variant: "error",
      });
      navigate("/payments");
    }

    setIsPaying(false);
  };

  const handleAutoSubmit = async () => {
    if (!isAutoSubmit()) {
      return;
    }

    if (!selectedIbanId) {
      return;
    }

    setIsPaying(true);

    try {
      const iban = await fetchIban(selectedIbanId);
      if (!iban) {
        return;
      }

      if (!iban.mandateApprovedAt) {
        startAutoSubmitInterval();
        return;
      }

      clearAutoSubmitInterval();
      handlePayNow();
    } catch (unknownError) {
      const error = unknownError as any;
      enqueueSnackbar({
        message: error?.message || "Something went wrong",
        variant: "error",
      });
    }
  };

  const isLoading = isFetchingIbans || isPaying || isFetchingPaymentRequest;

  if (!isLoading && ibans?.length === 0) {
    return (
      <Navigate
        to={`/payment/${paymentRequestId}/payment-method/mangopay/add-iban`}
        state={{ referrer: location.pathname + location.search }}
      />
    );
  }

  return (
    <MainCard content={false}>
      <Box py={2} px={3}>
        <Typography variant="h4" sx={sx.content.heading}>
          {getLocalizedText("tenant.payments.add_bank.title")}
        </Typography>
        <Typography
          variant="caption"
          dangerouslySetInnerHTML={{
            __html: getLocalizedText("tenant.payments.add_bank.info"),
          }}
        />
      </Box>
      <Divider />

      <Box p={3}>
        {isLoading ? (
          <SpinningLoader />
        ) : (
          <>
            <Stack direction="column" gap={1.5}>
              {ibans && ibans?.length > 0 && (
                <RadioButtonsGroup
                  id="iban"
                  label={getLocalizedText(
                    "payment_bank_account_list.section_header",
                  )}
                  value={selectedIbanId}
                  options={ibans.map(acc => ({
                    label: (
                      <Stack>
                        <Typography variant="h6" sx={sx.content.iban}>
                          {acc.iban}
                        </Typography>
                        {acc.ownerName && (
                          <Typography
                            variant="subtitle2"
                            sx={sx.content.accountName}
                          >
                            {acc.ownerName}
                          </Typography>
                        )}
                        <VopStatusBadge status={acc.mangoVopStatus} />
                      </Stack>
                    ),
                    value: acc.id,
                  }))}
                  onChange={id => setSelectedIbanId(Number(id))}
                />
              )}
              <Link
                label={`+ ${getLocalizedText(
                  "payment_bank_account_list.cta.add_bank_account",
                )}`}
                underlined={false}
                onClick={() =>
                  navigate(
                    `/payment/${paymentRequestId}/payment-method/mangopay/add-iban`,
                    {
                      state: { referrer: location.pathname + location.search },
                    },
                  )
                }
              />
            </Stack>
            <Stack direction="row" gap={1} mt={2.5}>
              <Button
                variant={EButtonVariants.Outlined}
                sx={sx.content.actionBtn}
                onClick={() =>
                  navigate(`/payment/${paymentRequestId}/payment-method`)
                }
                disabled={isLoading}
              >
                {getLocalizedText("system.previous")}
              </Button>
              <Button
                variant={EButtonVariants.Contained}
                sx={sx.content.actionBtn}
                onClick={handlePayNow}
                disabled={!selectedIbanId || isLoading}
              >
                {getLocalizedText("system.next")}
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </MainCard>
  );
};

export const IbanAccountSelect = withKycRequired(IbanAccountSelectBase);
